import { useState, useEffect, useRef, useCallback } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '../../lib/supabase';
import { generateCaptureQR } from '../../lib/qr-code';
import { createPhotoSession, deletePhotoSession } from '../../lib/photo-session';
import { getErrorMessage } from '../../lib/errors';
import { debug, error as loggerError, warn } from '../../lib/logger';

export interface PhonePhoto {
  url: string;
  index: number;
}

interface PhotoSessionPanelProps {
  onPhotosChange: (photos: PhonePhoto[]) => void;
  onSessionCreated?: (token: string) => void;
  maxPhotos: number;
  currentCount: number;
  isOpen: boolean;
  onClose: () => void;
  onToggle: () => void;
}

export default function PhotoSessionPanel({
  onPhotosChange,
  onSessionCreated,
  maxPhotos,
  currentCount,
  isOpen,
  onClose,
  onToggle,
}: PhotoSessionPanelProps) {
  const [token, setToken] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [photos, setPhotos] = useState<PhonePhoto[]>([]);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const currentCountRef = useRef(currentCount);
  currentCountRef.current = currentCount;
  const maxPhotosRef = useRef(maxPhotos);
  maxPhotosRef.current = maxPhotos;
  const photosLengthRef = useRef(photos.length);
  photosLengthRef.current = photos.length;

  // Sync photos to parent whenever they change
  useEffect(() => {
    onPhotosChange(photos);
  }, [photos, onPhotosChange]);

  // ── Create session + QR (runs once when modal opens) ──
  const startSession = useCallback(async () => {
    if (token) return; // already started
    setIsLoading(true);
    try {
      const newToken = await createPhotoSession();
      setToken(newToken);
      onSessionCreated?.(newToken);
      debug('[PhotoSessionPanel] Session created', { token: newToken });

      const qr = await generateCaptureQR(newToken);
      setQrDataUrl(qr);
    } catch (err) {
      loggerError('[PhotoSessionPanel] Failed to start session', { error: getErrorMessage(err) });
    } finally {
      setIsLoading(false);
    }
  }, [token, onSessionCreated]);

  // Start session when modal opens
  useEffect(() => {
    if (isOpen) startSession();
  }, [isOpen, startSession]);

  // ── Channel lifecycle (runs when token is set) ──
  useEffect(() => {
    if (!token) return;

    debug('[PhotoSessionPanel] Creating channel for token', { token });

    // Create channel with minimal config — let server decide defaults
    const channel = supabase.channel(`photo-session:${token}`);
    channelRef.current = channel;

    // Broadcast handlers
    channel.on('broadcast', { event: 'photo:add' }, (payload) => {
      const data = payload.payload as { index: number; url: string };
      const { index, url } = data;
      debug('[PhotoSessionPanel] Received photo:add', { index, url });
      if (index === undefined || !url) return;
      setIsConnected(true);
      const remaining = maxPhotosRef.current - currentCountRef.current - photosLengthRef.current;
      if (remaining <= 0) return;
      setPhotos((prev) => {
        if (prev.find((p) => p.index === index)) return prev;
        return [...prev, { url, index }].sort((a, b) => a.index - b.index);
      });
    });

    channel.on('broadcast', { event: 'photo:remove' }, (payload) => {
      const data = payload.payload as { index: number };
      debug('[PhotoSessionPanel] Received photo:remove', data);
      setPhotos((prev) => prev.filter((p) => p.index !== data.index));
    });

    channel.on('broadcast', { event: 'session:sync' }, (payload) => {
      const data = payload.payload as { photos: { index: number; url: string }[] };
      debug('[PhotoSessionPanel] Received session:sync', data);
      setIsConnected(true);
      if (data?.photos?.length) {
        setPhotos(data.photos.filter((p) => p.url).sort((a, b) => a.index - b.index));
      }
    });

    // Subscribe
      channel.subscribe((status) => {
        debug('[PhotoSessionPanel] Subscribe status', { status });
        if (status === 'SUBSCRIBED') {
          debug('[PhotoSessionPanel] Channel ready, waiting for phone');
        } else if (status === 'CLOSED') {
          warn('[PhotoSessionPanel] Channel closed');
        } else if (status === 'CHANNEL_ERROR') {
          loggerError('[PhotoSessionPanel] Channel error!');
        }
      });

    // Cleanup: unsubscribe when token changes or component unmounts
    return () => {
      debug('[PhotoSessionPanel] Cleaning up channel');
      channel.unsubscribe();
      channelRef.current = null;
    };
  }, [token]);

  // ── Close modal: keep everything alive, just hide UI ──
  const handleClose = useCallback(() => {
    // Keep channel, token, QR, photos — parent uses them for submit
    onClose();
  }, [onClose]);

  const remainingSlots = maxPhotos - currentCount - photos.length;

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        disabled={isLoading}
        className="font-nb font-bold px-4 py-2 bg-nb-white border-3 border-nb-black text-nb-black hover:bg-nb-yellow transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ borderRadius: 0 }}
      >
        📱 Add Photos from Phone
      </button>

      {isOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
          onClick={handleClose}
        >
          <div
            className="bg-nb-white border-3 border-nb-black p-6"
            style={{
              borderRadius: 0,
              maxWidth: '400px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 className="font-nb font-bold text-nb-black m-0 text-lg">
                📸 Photos from Phone
              </h3>
              <button
                type="button"
                onClick={handleClose}
                className="font-nb bg-transparent border-none text-nb-black/60 hover:text-nb-black cursor-pointer text-xl"
                style={{ borderRadius: 0 }}
              >
                ✕
              </button>
            </div>

            {isLoading && (
              <div className="text-center p-6 text-nb-black/60 font-nb">
                Generating QR code...
              </div>
            )}

            {qrDataUrl && !isLoading && (
              <div className="flex flex-col items-center mb-4">
                <img
                  src={qrDataUrl}
                  alt="Scan to capture photos"
                  className="border-3 border-nb-black"
                  style={{
                    width: '200px',
                    height: '200px',
                    borderRadius: 0,
                  }}
                />
                <p className="text-nb-black/60 mt-2 text-sm font-nb">
                  Scan with phone camera to add photos
                </p>
                <p className={
                  isConnected ? 'text-nb-black' : 'text-nb-black/40'
                } style={{ fontSize: '12px', marginTop: '4px' }}>
                  {isConnected ? '🟢 Phone connected' : '🔴 Waiting for phone...'}
                </p>
              </div>
            )}

            {photos.length > 0 && (
              <div className="mt-4">
                <p className="text-nb-black/60 text-xs mb-2 font-nb">
                  Photos from phone ({photos.length}/{photos.length + remainingSlots})
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                  {photos.map((photo) => (
                    <div key={photo.index} style={{ position: 'relative' }}>
                      <img
                        src={photo.url}
                        alt={`Photo ${photo.index + 1}`}
                        className="border-2 border-nb-black"
                        style={{
                          width: '100%',
                          aspectRatio: '1',
                          objectFit: 'cover',
                          borderRadius: 0,
                        }}
                      />
                      <span style={{
                        position: 'absolute',
                        top: '2px',
                        left: '2px',
                        background: '#000000',
                        color: 'white',
                        borderRadius: 0,
                        width: '18px',
                        height: '18px',
                        fontSize: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        📱
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {remainingSlots <= 0 && (
              <p className="text-nb-red font-nb font-bold text-sm text-center mt-4">
                Maximum {maxPhotos} photos reached
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
